import { useQuery, useMutation, useQueryClient } from 'react-query'
import { createHmac } from 'crypto'
import { gridClient, headers } from '@/utils/apiUtil'
import { WORKFLOW_CATALOGUE_QUERY_KEY } from '@/api/workflow-assignment-cache'
import { getWorkflowSigningKey } from '@/utils/envUtil'

// REQUESTS

export const unlockEmailRequest = async email => {
  const data = await gridClient.post(`/auth/request/unlock/${email}`)
  return data
}

export const getLocalIPAddressRequest = async () => {
  const response = await fetch('https://api.ipify.org?format=json')
  const data = await response.json()
  return data.ip
}

export const getSolutionsRequest = async () => {
  const { data } = await gridClient.get('/solution/live')
  return data
}

export const captureEmailRequest = async email => {
  const { data } = await gridClient.get(`/user/desktop/link/${email}`)
  return data
}

export const getTemplateForProductRequest = async productId => {
  const { data } = await gridClient.get(`/workflow/template/${productId}`)
  return data
}

export const getTemplateInputsForProductRequest = async productId => {
  const { data } = await gridClient.get(`/workflow/template/${productId}/input`)
  return data
}

export const getWorkflowPricingRequest = async payload => {
  const { data } = await gridClient.post('/pricing/workflow', payload)
  return data
}

export const getInvoicesRequest = async ({ customerId, payload }) => {
  const { data } = await gridClient.post(`/customer/statements/${customerId}`, payload)
  return data
}

const EARLIEST_STATEMENT_YEAR = 2024

function getStatementQueryYears() {
  const currentYear = new Date().getFullYear()
  const start = Math.min(EARLIEST_STATEMENT_YEAR, currentYear)
  return Array.from({ length: currentYear - start + 1 }, (_, i) => String(start + i))
}

export const inviteUserRequest = async payload => {
  const { data } = await gridClient.post('/user/invite', payload)
  return data
}

export const getWorkflowRunsCSVRequest = async ({ customerId, payload }) => {
  const { data } = await gridClient.post(`/workflow/runs/${customerId}?csv=true`, payload)
  return data
}

export const getAvailableEntitiesRequest = async payload => {
  const { data } = await gridClient.post('/solution/available/entities', payload)
  return data
}

export const retrieveFieldsForSolutionsRequest = async ({ solutionIds, entityTypes }) => {
  const payload: Record<string, unknown> = { solutionIds }
  if (entityTypes?.length) payload.entityTypes = entityTypes
  const { data } = await gridClient.post('/solution/product/infield', payload)
  return data
}

export const retrieveWorkflowTemplateRequest = async payload => {
  const { data } = await gridClient.post('/workflow/template', payload)
  return data
}

export const validateWorkflowRequest = async payload => {
  const { data } = await gridClient.post('/workflow/validate', payload)
  return data
}

export const searchProductSolutionsRequest = async payload => {
  const { data } = await gridClient.post('/product/search', payload)
  return data
}

export const initializeWorkflowRequest = async ({ payload }) => {
  const { data } = await gridClient.post('/workflow/initialize', payload)
  return data
}

export const registerEmailRequest = async payload => {
  const { data } = await gridClient.post('/auth/local/register', payload)
  return data
}

export const verifyEmailRequest = async payload => {
  const { data } = await gridClient.post('/auth/local/verify', payload)
  return data
}

export const resendTokenRequest = async payload => {
  const { data } = await gridClient.post('/auth/local/token', payload)
  return data
}

export const setPasswordRequest = async payload => {
  const { data } = await gridClient.patch('/auth/local/password', payload)
  return data
}

export const updateCustomerRequest = async ({ customerId, payload }) => {
  const { data } = await gridClient.patch(`/customer/${customerId}`, payload)
  return data
}

export const updateWorkflowRequest = async ({ workflowId, payload }) => {
  const { data } = await gridClient.patch(`/workflow/${workflowId}`, payload)
  return data
}

export const updateUserRoleRequest = async ({ id, payload }) => {
  const { data } = await gridClient.patch(`/user/${id}`, payload)
  return data
}

// update name and status
export const updateWorkflowGeneralRequest = async ({ workflowId, payload }) => {
  const { data } = await gridClient.patch(`/workflow/${workflowId}/general`, payload)
  return data
}

export const deactivateWorkflowRequest = async ({ workflowId }) => {
  const { data } = await gridClient.delete(`/workflow/${workflowId}`)
  return data
}

export const deleteUserRequest = async ({ userId }) => {
  const { data } = await gridClient.delete(`/user/${userId}`)
  return data
}

export const loginRequest = async payload => {
  const { data } = await gridClient.post('/auth/local/login', payload)
  return data
}

export const exchangeSsoCodeRequest = async code => {
  const { data } = await gridClient.post('/auth/sso/exchange', { code })
  return data
}

export const exchangeGoogleCodeRequest = async code => {
  const { data } = await gridClient.post('/auth/google/exchange', { code })
  return data
}

export const logoutRequest = async () => {
  const { data } = await gridClient.post('/auth/logout', {})
  return data
}

export const updateUserRequest = async ({ userId, payload }) => {
  const { data } = await gridClient.patch(`/user/${userId}`, payload)
  return data
}

export const verify2FAEmailChangeRequest = async ({ userId, payload }) => {
  const { data } = await gridClient.patch(`settings/${userId}/email/verify`, payload)
  return data
}

export const updateEmailRequest = async ({ userId, payload }) => {
  const { data } = await gridClient.patch(`/settings/${userId}/email`, payload)
  return data
}

export const updatePasswordRequest = async ({ userId, payload }) => {
  const { data } = await gridClient.patch(`/settings/${userId}/password`, payload)
  return data
}

export const getWorkflowFromIdRequest = async workflowId => {
  const { data } = await gridClient.get(`/workflow/${workflowId}?format=template`)
  return data
}

export const getAPIKeysForCustomerRequest = async customerId => {
  const { data } = await gridClient.get(`/api-key/customer/${customerId}`)
  return data
}

export const getRolesRequest = async () => {
  const { data } = await gridClient.get('/role')
  return data
}

export const getCompanyUsersRequest = async customerId => {
  const { data } = await gridClient.get(`/user/customer/${customerId}`)
  return data
}

export const getCompanyRolesRequest = async () => {
  const { data } = await gridClient.get('/settings/company-roles')
  return data
}

export const getCompanyInvoicesRequest = async customerId => {
  const { data } = await gridClient.get(`/customer/statements/${customerId}/metrics`)
  return data
}

export const createAPIKeyRequest = async payload => {
  const { data } = await gridClient.post('/api-key', payload)
  return data
}

export const updateAPIKeyRequest = async ({ keyId, payload }) => {
  const { data } = await gridClient.patch(`/api-key/${keyId}`, payload)
  return data
}

export const deleteAPIKeyRequest = async keyId => {
  const { data } = await gridClient.delete(`/api-key/${keyId}`)
  return data
}

export const getIPAddressForCustomerRequest = async customerId => {
  const { data } = await gridClient.get(`/ip-address/customer/${customerId}`)
  return data
}

export const createIPAddressRequest = async payload => {
  const { data } = await gridClient.post('/ip-address', payload)
  return data
}

export const deleteIPAddressRequest = async addressId => {
  const { data } = await gridClient.delete(`/ip-address/${addressId}`)
  return data
}

export const previewNewWorkflowAPIRequest = async payload => {
  const { data } = await gridClient.post('/workflow/initialize/preview/input', payload)
  return data
}

export const previewWorkflowAPIRequest = async workflowId => {
  const { data } = await gridClient.post(`/workflow/preview/input/${workflowId}`, {})
  return data
}

export const getWorkflowInsightsPreviewRequest = async ({
  payload,
  apiKeyId,
}) => {
  try {
    const hashSigningKey = () => createHmac('sha256', getWorkflowSigningKey())
      .update(JSON.stringify(payload))
      .digest('hex')

    const hashedKey = hashSigningKey()

    const insightHeaders = {
      ...headers(),
      'x-signature': hashedKey,
      'x-api-key-id': apiKeyId,
      'x-api-version': process.env.NEXT_PUBLIC_API_VERSION || '1.0',
    }
    const { data } = await gridClient.post('/insights', payload, {
      headers: insightHeaders,
    })
    return data
  } catch (e) {
    return {
      code: e.code || 'ERROR',
      message: e.message || 'An error occurred while running the API',
      details: e.details || {},
      error: true,
    }
  }
}

export const registerEmailForTokenRequest = async email => {
  const { data } = await gridClient.patch(`registration/send/token/${email}`)
  return data
}

export const validateTokenRequest = async ({ email, payload }) => {
  const { data } = await gridClient.patch(`registration/validate/token/${email}`, payload)
  return data
}

export const resetPasswordRequest = async ({ token, payload }) => {
  const { data } = await gridClient.post(`/auth/reset/password/${token}`, payload)
  return data
}

export const unlockAccountRequest = async ({ token, payload }) => {
  const { data } = await gridClient.post(`/auth/unlock/account/${token}`, payload)
  return data
}

// HOOKS

export function getSolutionsHook() {
  return useQuery(['get-solutions'], getSolutionsRequest, { refetchOnWindowFocus: false })
}

export function getWorkflowFromIdHook(workflowId, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery(['get-workflow', workflowId], async () => {
    const { data } = await gridClient.get(`/workflow/${workflowId}?format=template`)
    return data
  }, { refetchOnWindowFocus: false, enabled })
}

export function getTemplateForProductHook(productId) {
  return useQuery(['get-product-template', productId], () => getTemplateForProductRequest(productId), { refetchOnWindowFocus: false })
}

export function getTemplateInputsForProductHook(productId) {
  return useQuery(['get-product-inputs', productId], () => getTemplateInputsForProductRequest(productId), { refetchOnWindowFocus: false })
}

export function getAPIKeysForCustomerHook(customerId) {
  return useQuery(['get-api-keys', customerId], () => getAPIKeysForCustomerRequest(customerId), { refetchOnWindowFocus: false })
}

export function getCompanyUsersHook(customerId, { enabled }) {
  return useQuery(['get-users', customerId], () => getCompanyUsersRequest(customerId), { refetchOnWindowFocus: false, enabled })
}

export function getRolesHook({ enabled }) {
  return useQuery(['get-roles'], () => getRolesRequest(), { refetchOnWindowFocus: false, enabled })
}

export function getCompanyRolesHook(customerId, { enabled }) {
  return useQuery(['get-company-roles', customerId], () => getCompanyRolesRequest(), { refetchOnWindowFocus: false, enabled })
}

export function getCompanyInvoicesHook(customerId, { enabled, onSuccess }) {
  return useQuery(['get-invoices', customerId], () => getCompanyInvoicesRequest(customerId), { refetchOnWindowFocus: false, enabled, onSuccess })
}

export function retrieveFieldsForSolutionsHook() {
  return useMutation(retrieveFieldsForSolutionsRequest)
}

export function retrieveWorkflowTemplateHook({ onSuccess }: { onSuccess: (data: unknown) => void }) {
  return useMutation(retrieveWorkflowTemplateRequest, { onSuccess })
}

export function validateWorkflowHook() {
  return useMutation(validateWorkflowRequest)
}

export function getWorkflowPricingHook() {
  return useMutation(getWorkflowPricingRequest)
}

export function searchProductSolutionsHook() {
  return useMutation(searchProductSolutionsRequest)
}

export function registerEmailHook({ onSuccess }) {
  return useMutation(registerEmailRequest, { onSuccess })
}

export function verifyEmailHook({ onSuccess }) {
  return useMutation(verifyEmailRequest, { onSuccess })
}

export function resendTokenHook() {
  return useMutation(resendTokenRequest)
}

export function setPasswordHook() {
  return useMutation(setPasswordRequest)
}

export function updateCustomerHook() {
  return useMutation(updateCustomerRequest)
}

export function useUpdateUser(customerId) {
  const queryClient = useQueryClient()
  return useMutation(updateUserRequest, {
    onSuccess: () => {
      queryClient.invalidateQueries(['get-users', customerId])
    },
  })
}

export function verify2FAEmailChangeHook() {
  return useMutation(verify2FAEmailChangeRequest)
}

export function updateEmailHook() {
  return useMutation(updateEmailRequest)
}

export function updatePasswordHook() {
  return useMutation(updatePasswordRequest)
}

export function createAPIKeyHook() {
  return useMutation(createAPIKeyRequest)
}

export function updateAPIKeyHook() {
  return useMutation(updateAPIKeyRequest)
}

export function deleteAPIKeyHook() {
  return useMutation(deleteAPIKeyRequest)
}

export function updateWorkflowGeneralHook() {
  return useMutation(async ({ workflowId, payload }: { workflowId: string, payload: Record<string, unknown> }) => {
    const { data } = await gridClient.patch(`/workflow/${workflowId}/general`, payload)
    return data
  })
}

export function deactivateWorkflowHook() {
  return useMutation(deactivateWorkflowRequest)
}

export function useDeleteUser(onSuccess) {
  return useMutation(deleteUserRequest, {
    onSuccess,
  })
}

export function updateWorkflowHook() {
  const queryClient = useQueryClient()
  return useMutation(updateWorkflowRequest, {
    onSuccess: (_data, variables) => queryClient.invalidateQueries(['get-workflow', variables.workflowId]),
  })
}

export function initializeWorkflowHook() {
  return useMutation(initializeWorkflowRequest)
}

export function getIPAddressForCustomerHook(customerId) {
  return useQuery(['get-ip-addresses', customerId], () => getIPAddressForCustomerRequest(customerId), { refetchOnWindowFocus: false })
}

export function captureEmailHook(email) {
  return useQuery(['capture-email', email], () => captureEmailRequest(email), { refetchOnWindowFocus: false, enabled: false })
}

export function createIPAddressHook() {
  return useMutation(createIPAddressRequest)
}

export function deleteIPAddressHook() {
  return useMutation(deleteIPAddressRequest)
}

export function previewNewWorkflowAPIHook() {
  return useMutation(previewNewWorkflowAPIRequest)
}

export function previewWorkflowAPIHook() {
  return useMutation(previewWorkflowAPIRequest)
}

export function getWorkflowInsightsPreviewHook() {
  return useMutation(getWorkflowInsightsPreviewRequest)
}

export function unlockEmailHook() {
  return useMutation(unlockEmailRequest)
}

export function getInvoicesHook(customerId, options) {
  const calendarYear = new Date().getFullYear()
  return useQuery(
    ['get-invoices', customerId, calendarYear],
    () => getInvoicesRequest({
      customerId,
      payload: {
        statuses: ['PAID', 'UNPAID'],
        years: getStatementQueryYears(),
        download: false,
        take: 20,
        skip: 0,
        orderBy: {
          statementDate: 'asc',
          amount: 'desc',
        },
      },
    }),
    options,
  )
}

export function inviteUserHook({ onSuccess, onError }) {
  return useMutation(inviteUserRequest, { onSuccess, onError })
}

export function getWorkflowCSVHook() {
  return useMutation(getWorkflowRunsCSVRequest)
}

export function getAvailableEntitiesHook() {
  return useMutation(getAvailableEntitiesRequest)
}

export function registerEmailForTokenHook() {
  return useMutation(registerEmailForTokenRequest)
}

export function validateTokenHook() {
  return useMutation(validateTokenRequest)
}

export function resetPasswordHook() {
  return useMutation(resetPasswordRequest)
}

export function unlockAccountHook() {
  return useMutation(unlockAccountRequest)
}

// Workflow catalogue (POST /customer/:id/workflows) — unlike `customer.workflows`,
// this includes workflows assigned to the customer's sub-organizations and the
// `isAssigned` / `assignedTo` (sub-org name) fields used by the sidebar labels.
export const getWorkflowCatalogueRequest = async ({ customerId, payload }) => {
  const { data } = await gridClient.post(`/customer/${customerId}/workflows`, payload)
  return data
}

export const DEFAULT_WORKFLOW_CATALOGUE_PAYLOAD = { sortBy: 'name', order: 'asc' }

// `payload` carries the sort (`sortBy`/`order`) plus the optional filters
// (`from`, `status`, `subOrganizationIds`) and is part of the query key so the
// sidebar refetches whenever the user changes sort or filters.
export function getWorkflowCatalogueHook(customerId, { enabled, payload = DEFAULT_WORKFLOW_CATALOGUE_PAYLOAD }) {
  return useQuery(
    [WORKFLOW_CATALOGUE_QUERY_KEY, customerId, payload],
    () => getWorkflowCatalogueRequest({ customerId, payload }),
    { refetchOnWindowFocus: false, enabled },
  )
}
